import { modal } from 'components/Modals/ModalManager';
import { makeAutoObservable } from 'mobx';
import router from 'next/router';
import toast from 'react-hot-toast';
import { stores } from 'state/stores';
import { t } from 'translations/t';
import API from 'utils/API';
import { clearAllUserData } from 'utils/logout';

export class AcceptInviteViewModel {
  viewState: 'signup' | 'login';

  inviteShortId?: string;
  coachUsername: string;
  coachFullname?: string;
  inviteeUsername?: string;

  isAcceptingInvite = false;
  isDecliningInvite = false;
  isProcessingLoginOrSignup = false;

  constructor({
    inviteShortId,
    coachUsername,
    coachFullname,
    inviteeUsername,
  }: {
    inviteShortId?: string;
    coachUsername: string;
    coachFullname?: string;
    inviteeUsername?: string;
  }) {
    this.inviteShortId = inviteShortId;
    this.coachUsername = coachUsername;
    this.coachFullname = coachFullname;
    this.inviteeUsername = inviteeUsername;
    if (!!this.inviteeUsername) {
      this.viewState = 'login';
    } else {
      this.viewState = 'signup';
    }
    makeAutoObservable(this);

    if (stores.account.isActivelyCoached) {
      toast.error(t('web.coach.acceptInvite.alreadyHaveCoachError'), { duration: 5000 });
      router.push('/coach');
    }
  }

  get showDeclineButton() {
    return !!this.inviteShortId;
  }

  private onAcceptInvite = async () => {
    this.isAcceptingInvite = true;
    try {
      if (!this.inviteShortId) {
        await API.getBecomeClient(this.coachUsername);
      } else {
        await API.acceptCoachInviteWithShortId(this.inviteShortId!);
      }
    } catch (error) {
      throw error;
    } finally {
      this.isAcceptingInvite = false;
    }
  };

  acceptInvite = async () => {
    await this.onAcceptInvite();
    this.isProcessingLoginOrSignup = false;
    modal.dismissCoachInviteModal();
    /** and switch to the tab to show the instructions */
    router.push('/coach').then(() => {
      stores.account.coach_client_status = 'coached-active-plan';
      stores.coach.fetch();
    });
  };

  declineInvite = async () => {
    if (!this.inviteShortId) return;
    this.isDecliningInvite = true;
    try {
      await API.declineCoachInviteWithShortId(this.inviteShortId);
      this.isProcessingLoginOrSignup = false;
      toast.success(t('web.coach.acceptInvite.inviteDeclined'));
      modal.dismissCoachInviteModal();
      router.push('/').then(() => stores.coach.fetch());
    } catch (error) {
      throw error;
    } finally {
      this.isDecliningInvite = false;
    }
  };

  onSignUpOrSignInComplete = async ({ isNewUser }: { isNewUser: boolean }) => {
    try {
      await toast.promise(this.onAcceptInvite(), {
        loading: t('web.coach.acceptInvite.signInOrUpComplete.loading'),
        success: t('web.coach.acceptInvite.signInOrUpComplete.success'),
        error: t('web.coach.acceptInvite.signInOrUpComplete.error'),
      });
      if (isNewUser) {
        router.push('/coach/onboarding/username');
      } else {
        router.push('/coach');
      }
    } catch (error) {
      clearAllUserData();
    }
  };
}
